import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle,
  DialogContent, DialogActions, Tabs, Tab, Grid, Paper, Alert, MenuItem
} from "@mui/material";
import { Refresh, Edit, AddCircle, RemoveCircle, Warning, CheckCircle, Inventory } from "@mui/icons-material";
import API from "../services/api";
import toast from "react-hot-toast";

export default function StockManagement() {
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState({});
  const [lowStock, setLowStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [adjustDialog, setAdjustDialog] = useState({ open: false, product: null });
  const [thresholdDialog, setThresholdDialog] = useState({ open: false, product: null });
  const [adjustForm, setAdjustForm] = useState({ tip: "giris", miktar: "", notlar: "" });
  const [thresholdForm, setThresholdForm] = useState({ min_stock: "", reorder_qty: "" });

  const loadAll = useCallback(async () => {
    try {
      const [s, l, m] = await Promise.all([
        API.get("/api/admin/stock/summary"),
        API.get("/api/admin/stock/low-stock?limit=200"),
        API.get("/api/admin/stock/movements?days=7&limit=100")
      ]);
      setSummary(s.data);
      setLowStock(l.data.items || []);
      setMovements(m.data || []);
    } catch (e) { toast.error("Veri yüklenemedi"); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openAdjust = (product) => {
    setAdjustForm({ tip: "giris", miktar: "", notlar: "" });
    setAdjustDialog({ open: true, product });
  };

  const openThreshold = (product) => {
    setThresholdForm({ min_stock: product.min_stock || 10, reorder_qty: product.reorder_qty || 0 });
    setThresholdDialog({ open: true, product });
  };

  const handleAdjust = async () => {
    if (!adjustForm.miktar) return toast.error("Miktar gerekli");
    try {
      await API.post(`/api/admin/stock/adjust/${adjustDialog.product.id}`, {
        tip: adjustForm.tip, miktar: parseFloat(adjustForm.miktar), notlar: adjustForm.notlar
      });
      toast.success("Stok güncellendi");
      setAdjustDialog({ open: false, product: null });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.error || "Hata"); }
  };

  const handleThreshold = async () => {
    try {
      await API.put(`/api/admin/stock/threshold/${thresholdDialog.product.id}`, {
        min_stock: parseInt(thresholdForm.min_stock) || 0,
        reorder_qty: parseInt(thresholdForm.reorder_qty) || 0
      });
      toast.success("Eşik güncellendi");
      setThresholdDialog({ open: false, product: null });
      loadAll();
    } catch (e) { toast.error("Hata"); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700}>📦 Stok Yönetimi</Typography>
        <Button startIcon={<Refresh />} onClick={loadAll}>Yenile</Button>
      </Stack>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Inventory sx={{ fontSize: 32, color: '#1565C0' }} />
            <Typography variant="h5" fontWeight={700}>{summary.total_products || 0}</Typography>
            <Typography variant="caption">Toplam Ürün</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
            <Warning sx={{ fontSize: 32, color: '#D32F2F' }} />
            <Typography variant="h5" fontWeight={700}>{summary.out_of_stock || 0}</Typography>
            <Typography variant="caption">Stokta Yok</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff8e1' }}>
            <Warning sx={{ fontSize: 32, color: '#F57C00' }} />
            <Typography variant="h5" fontWeight={700}>{summary.low_stock || 0}</Typography>
            <Typography variant="caption">Düşük Stok</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
            <CheckCircle sx={{ fontSize: 32, color: '#4CAF50' }} />
            <Typography variant="h5" fontWeight={700}>{summary.healthy_stock || 0}</Typography>
            <Typography variant="caption">Sağlıklı</Typography>
          </Paper>
        </Grid>
      </Grid>

      {summary.total_value && (
        <Alert severity="info" sx={{ mb: 2 }}>
          💰 Toplam Stok Değeri: <b>{Number(summary.total_value).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</b>
          {summary.total_units && ` | Toplam Birim: ${Math.round(summary.total_units).toLocaleString("tr-TR")}`}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Düşük Stok (${lowStock.length})`} />
        <Tab label="Stok Hareketleri (7 gün)" />
      </Tabs>

      {tab === 0 && (
        <Card>
          <CardContent>
            {lowStock.length === 0 ? (
              <Alert severity="success">Tüm ürünler sağlıklı stok seviyesinde 🎉</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Ürün</b></TableCell>
                    <TableCell><b>Kategori</b></TableCell>
                    <TableCell align="right"><b>Stok</b></TableCell>
                    <TableCell align="right"><b>Min</b></TableCell>
                    <TableCell align="right"><b>Sipariş</b></TableCell>
                    <TableCell align="center"><b>İşlem</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lowStock.map(p => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.category}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${p.stock} ${p.unit || ""}`}
                          size="small"
                          color={p.stock === 0 ? "error" : "warning"}
                        />
                      </TableCell>
                      <TableCell align="right">{p.min_stock}</TableCell>
                      <TableCell align="right">{p.reorder_qty || "—"}</TableCell>
                      <TableCell align="center">
                        <Button size="small" startIcon={<AddCircle />} onClick={() => openAdjust(p)}>Stok</Button>
                        <Button size="small" startIcon={<Edit />} onClick={() => openThreshold(p)}>Eşik</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Card>
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Tarih</b></TableCell>
                  <TableCell><b>Ürün</b></TableCell>
                  <TableCell><b>Tip</b></TableCell>
                  <TableCell align="right"><b>Miktar</b></TableCell>
                  <TableCell align="right"><b>Önce → Sonra</b></TableCell>
                  <TableCell><b>Kaynak</b></TableCell>
                  <TableCell><b>Notlar</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map(m => (
                  <TableRow key={m.id} hover>
                    <TableCell>{new Date(m.created_at).toLocaleString("tr-TR")}</TableCell>
                    <TableCell>{m.urun_adi || `#${m.urun_id}`}</TableCell>
                    <TableCell>
                      <Chip label={m.tip} size="small" color={m.tip === "giris" ? "success" : "error"} />
                    </TableCell>
                    <TableCell align="right">{m.miktar}</TableCell>
                    <TableCell align="right">{m.onceki_stok} → {m.sonraki_stok}</TableCell>
                    <TableCell>{m.kaynak}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{m.notlar || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Stok ayarla dialog */}
      <Dialog open={adjustDialog.open} onClose={() => setAdjustDialog({ open: false, product: null })} fullWidth maxWidth="xs">
        <DialogTitle>Stok Ayarla: {adjustDialog.product?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField select label="İşlem" value={adjustForm.tip} onChange={(e) => setAdjustForm({ ...adjustForm, tip: e.target.value })}>
              <MenuItem value="giris">Stok Girişi (+)</MenuItem>
              <MenuItem value="cikis">Stok Çıkışı (-)</MenuItem>
            </TextField>
            <TextField label="Miktar" type="number" value={adjustForm.miktar} onChange={(e) => setAdjustForm({ ...adjustForm, miktar: e.target.value })} />
            <TextField label="Notlar" multiline rows={2} value={adjustForm.notlar} onChange={(e) => setAdjustForm({ ...adjustForm, notlar: e.target.value })} />
            <Typography variant="caption" color="text.secondary">
              Mevcut: {adjustDialog.product?.stock || 0} → Yeni: {
                adjustForm.miktar ? (
                  adjustForm.tip === "giris"
                    ? (adjustDialog.product?.stock || 0) + parseFloat(adjustForm.miktar)
                    : (adjustDialog.product?.stock || 0) - parseFloat(adjustForm.miktar)
                ) : "—"
              }
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialog({ open: false, product: null })}>İptal</Button>
          <Button variant="contained" onClick={handleAdjust}>Uygula</Button>
        </DialogActions>
      </Dialog>

      {/* Eşik ayarla dialog */}
      <Dialog open={thresholdDialog.open} onClose={() => setThresholdDialog({ open: false, product: null })} fullWidth maxWidth="xs">
        <DialogTitle>Eşik Ayarla: {thresholdDialog.product?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Min. Stok (uyarı eşiği)" type="number" value={thresholdForm.min_stock} onChange={(e) => setThresholdForm({ ...thresholdForm, min_stock: e.target.value })} />
            <TextField label="Sipariş Miktarı (reorder)" type="number" value={thresholdForm.reorder_qty} onChange={(e) => setThresholdForm({ ...thresholdForm, reorder_qty: e.target.value })} />
            <Typography variant="caption" color="text.secondary">
              Stok ≤ Min Stok olduğunda düşük stok listesinde görünür.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThresholdDialog({ open: false, product: null })}>İptal</Button>
          <Button variant="contained" onClick={handleThreshold}>Kaydet</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
